import { useEffect, useState } from 'react';
import { observer } from 'mobx-react';
import { useTranslation } from 'react-i18next';
import {
  Alert,
  Box,
  Button,
  Drawer,
  FormControl,
  FormControlLabel,
  Grid,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  Switch,
  TextField,
  Toolbar,
  Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';

import PrinterTypes from '../../model/PrinterTypes';
import Printer from '../../model/Printer';
import { usePrinterStore } from '../../stores';

const PRINTER_TYPES = [PrinterTypes.SUNMI_CLOUD, PrinterTypes.STAR];

interface PrinterEditDrawerProps {
  open: boolean;
  onClose: () => void;
  printer: Printer | null;
}

const PrinterEditDrawer = observer(
  ({ open, onClose, printer }: PrinterEditDrawerProps): JSX.Element => {
    const [printerType, setPrinterType] = useState(printer ? printer.type : PrinterTypes.STAR);
    const [name, setName] = useState(printer ? printer.name : '');
    const [ip, setIp] = useState(printer ? printer.ip : '');
    const [serialNumber, setSerialNumber] = useState(printer ? printer.serialNumber : '');
    const [isReceiptPrinter, setIsReceiptPrinter] = useState(
      printer ? printer.isReceiptPrinter : false,
    );
    const printerStore = usePrinterStore();

    const { t } = useTranslation();

    useEffect(() => {
      if (printer) {
        setPrinterType(printer.type);
        setName(printer.name);
        setIp(printer.ip);
        setSerialNumber(printer.serialNumber);
        setIsReceiptPrinter(printer.isReceiptPrinter);
      }
    }, [printer]);

    const handleClose = () => {
      setPrinterType(PrinterTypes.STAR);
      setName('');
      setIp('');
      onClose();
    };

    return (
      <Drawer anchor="right" open={open} onClose={handleClose}>
        <Box sx={{ width: 600, paddingRight: 4, paddingLeft: 4 }}>
          <Toolbar
            disableGutters
            sx={{
              marginTop: 12,
              display: 'flex',
              flexDirection: 'column',
            }}
          >
            {printerStore.errorMessage && (
              <Box sx={{ marginTop: 2, marginBottom: 2, width: '100%' }}>
                <Alert severity="error">{printerStore.errorMessage}</Alert>
              </Box>
            )}
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                width: '100%',
              }}
            >
              <Typography variant="h4">
                {printer ? t('printerScreen.editPrinter') : t('printerScreen.addPrinter')}
              </Typography>
              <IconButton onClick={handleClose}>
                <CloseIcon />
              </IconButton>
            </Box>
          </Toolbar>
          <Grid container spacing={2} sx={{ marginTop: 2, marginBottom: 2 }}>
            <Grid item xs={12}>
              <FormControl fullWidth>
                <InputLabel id="printer-type-label">{t('printerScreen.selectType')}</InputLabel>
                <Select
                  labelId="printer-type-label"
                  id="printer-type-select"
                  value={printerType}
                  defaultValue=""
                  label={t('printerScreen.selectType')}
                  onChange={(e) => setPrinterType(e.target.value as PrinterTypes)}
                >
                  {PRINTER_TYPES.map((key: PrinterTypes) => (
                    <MenuItem key={key} value={key}>
                      {t(`printerScreen.printerTypes.${key}`)}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <>
              <Grid item xs={12}>
                <TextField
                  id="name"
                  label={t('printerScreen.name')}
                  variant="outlined"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  fullWidth
                />
              </Grid>
              {printerType === PrinterTypes.STAR && (
                <Grid item xs={12}>
                  <TextField
                    id="ip"
                    label={t('printerScreen.ip')}
                    variant="outlined"
                    value={ip}
                    onChange={(e) => setIp(e.target.value)}
                    fullWidth
                  />
                </Grid>
              )}
              {printerType === PrinterTypes.SUNMI_CLOUD && (
                <Grid item xs={12}>
                  <TextField
                    id="serialNumber"
                    label={t('printerScreen.serialNumber')}
                    variant="outlined"
                    value={serialNumber}
                    onChange={(e) => setSerialNumber(e.target.value)}
                    fullWidth
                  />
                </Grid>
              )}
              <Grid item xs={12}>
                <FormControlLabel
                  label={t('printerScreen.isReceiptPrinter')}
                  control={
                    <Switch
                      color="secondary"
                      id="isReceiptPrinter"
                      checked={isReceiptPrinter}
                      onChange={() => setIsReceiptPrinter(!isReceiptPrinter)}
                    />
                  }
                />
              </Grid>
            </>
          </Grid>
        </Box>
        <Box
          sx={{
            marginTop: 'auto',
            marginLeft: 'auto',
            padding: '1rem 2rem',
          }}
        >
          <Button
            onClick={() => {
              if (printer) {
                // update printer
                printerStore
                  .updatePrinter({
                    id: printer.id,
                    type: printerType,
                    name,
                    serialNumber,
                    ip,
                    isReceiptPrinter,
                  })
                  .then(() => {
                    handleClose();
                  });
              } else {
                // add printer
                printerStore
                  .addPrinter({
                    type: printerType,
                    name,
                    serialNumber,
                    ip,
                    isReceiptPrinter,
                  })
                  .then(() => {
                    handleClose();
                  });
              }
            }}
            variant="contained"
          >
            {printer ? t('printerScreen.save') : t('printerScreen.add')}
          </Button>
          {printer && (
            <Button
              onClick={() => {
                printerStore.removePrinter(printer.id).then(() => {
                  handleClose();
                });
              }}
              variant="contained"
              color="error"
            >
              {t('printerScreen.remove')}
            </Button>
          )}
        </Box>
      </Drawer>
    );
  },
);

export default PrinterEditDrawer;
